import React, { useEffect, useRef, useState, useMemo } from "react";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import {
  Stack,
  Typography,
  Pagination,
  Box,
  TextField,
  InputAdornment,
  FormControl,
  Select,
  MenuItem,
  SelectChangeEvent,
} from "@mui/material";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { useSearchParams } from "react-router-dom";
import SearchIcon from "@mui/icons-material/Search";

interface draggableListProps {
  itemData: any;
  onOrderUpdate: (order: any) => void;
  onEdit: (item: any) => void;
  onDelete: (id: number) => void;
  code: string;
  name: string;
  itemsPerPage?: number;
}

const DraggableItemList = ({
  itemData,
  onOrderUpdate,
  onEdit,
  onDelete,
  code,
  name,
  itemsPerPage = 10,
}: draggableListProps) => {
  const [items, setItems] = useState(itemData);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");
  const [pageSize, setPageSize] = useState(itemsPerPage);
  const [searchParams, setSearchParams] = useSearchParams();
  const pageSizeOptions = [5, 10, 20];
  const productId = searchParams.get("productId");
  const itemsFingerprintRef = useRef("");

  const getItemsFingerprint = (list: unknown) => {
    if (!Array.isArray(list)) {
      return "";
    }
    return list
      .map(
        (item: any) =>
          `${item?.id ?? ""}:${item?.[code] ?? ""}:${item?.[name] ?? ""}:${item?.position ?? ""}`,
      )
      .join("|");
  };

  useEffect(() => {
    const nextFingerprint = getItemsFingerprint(itemData);
    if (nextFingerprint === itemsFingerprintRef.current) {
      return;
    }
    itemsFingerprintRef.current = nextFingerprint;
    setItems(itemData);
  }, [itemData]);

  useEffect(() => {
    const pageFromUrl = Number(searchParams.get("currentPage"));
    if (Number.isFinite(pageFromUrl) && pageFromUrl > 0) {
      setCurrentPage(pageFromUrl);
    }
  }, []);

  useEffect(() => {
    setSearchParams((prev) => ({
      ...Object.fromEntries(prev.entries()),
      productId: productId?.toString() ?? "1",
      currentPage: currentPage.toString(),
      pageSize: pageSize.toString(),
    }));
  }, [currentPage, pageSize, productId, setSearchParams]);

  const isSampleItem = (item: any) => {
    const codeValue = item[code]?.toString() || "";
    const nameValue = item[name]?.toString() || "";
    return (
      codeValue.includes("sample_test_agilysys") ||
      nameValue.includes("sample_test_agilysys")
    );
  };

  const filteredItems = useMemo(() => {
    let result = Array.isArray(items) ? items : [];

    // Filter by search query if provided
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.filter((item: any) => {
        const codeValue = item[code]?.toString().toLowerCase() || "";
        const nameValue = item[name]?.toString().toLowerCase() || "";
        return codeValue.includes(query) || nameValue.includes(query);
      });
    }

    // Check if there are non-sample items
    const nonSampleItems = result.filter((item: any) => !isSampleItem(item));

    // If there are non-sample items, filter out sample items; otherwise keep all
    if (nonSampleItems.length > 0) {
      return nonSampleItems;
    }

    return result;
  }, [items, searchQuery, code, name]);

  const totalPages = Math.ceil(filteredItems.length / pageSize);
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const paginatedItems = filteredItems.slice(startIndex, endIndex);

  const handlePageChange = (
    event: React.ChangeEvent<unknown>,
    page: number,
  ) => {
    setCurrentPage(page);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value);
    setCurrentPage(1);
  };

  const handlePageSizeChange = (event: SelectChangeEvent<number>) => {
    setPageSize(Number(event.target.value));
    setCurrentPage(1);
  };

  const checkSampleData = (data: string) => {
    return data?.includes("sample_test_agilysys");
  };

  const handleDragEnd = (result: any) => {
    if (!result.destination) return;
    if (result.destination.index === result.source.index) return;

    const sourceGlobal = startIndex + result.source.index;
    const destGlobal = startIndex + result.destination.index;

    const filteredCopy = Array.from(filteredItems);
    const [reorderedItem] = filteredCopy.splice(sourceGlobal, 1);
    filteredCopy.splice(destGlobal, 0, reorderedItem);

    const filteredIdSet = new Set(filteredItems.map((item: any) => item.id));
    let nextFiltered = 0;
    const mergedItems = items.map((item: any) => {
      if (filteredIdSet.has(item.id)) {
        return filteredCopy[nextFiltered++];
      }
      return item;
    });

    const updatedItems = mergedItems.map((item: any, index: number) => ({
      ...(typeof item === "object" ? item : {}),
      position: index + 1,
    }));

    setItems(updatedItems);
    itemsFingerprintRef.current = getItemsFingerprint(updatedItems);
    onOrderUpdate(updatedItems);
  };

  return (
    <Stack direction="column">
      <Box mb={2}>
        <TextField
          fullWidth
          size="small"
          placeholder="Search..."
          value={searchQuery}
          onChange={handleSearchChange}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
      </Box>
      <DragDropContext onDragEnd={handleDragEnd}>
        <Droppable droppableId="items">
          {(provided) => (
            <div
              className="dndFields"
              {...provided.droppableProps}
              ref={provided.innerRef}
              style={{ width: "100%" }}
            >
              {paginatedItems.length > 0 ? (
                paginatedItems.map((item: any, index: number) => (
                  <Draggable
                    key={item.id}
                    draggableId={`item-${item.id}`}
                    index={index}
                  >
                    {(provided) => (
                      <Stack
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        direction="row"
                        gap="20px"
                        border="1px solid gray"
                        p={1}
                        borderRadius="10px"
                        justifyContent="space-between"
                        mb={1}
                      >
                        <Stack direction="row" gap="20px" id={`dnd_${item.id}`}>
                          <div {...provided.dragHandleProps}>
                            <DragIndicatorIcon sx={{ cursor: "pointer" }} />
                          </div>
                          <Typography
                            sx={{
                              color: checkSampleData(item[code]?.toString())
                                ? "red"
                                : "black",
                              textDecoration: checkSampleData(
                                item[code]?.toString(),
                              )
                                ? "line-through"
                                : "none",
                            }}
                          >
                            {item[code]}
                          </Typography>
                          {item?.[name] && (
                            <Typography
                              sx={{
                                color: checkSampleData(item[name]?.toString())
                                  ? "red"
                                  : "black",
                                textDecoration: checkSampleData(
                                  item[name].toString(),
                                )
                                  ? "line-through"
                                  : "none",
                              }}
                            >
                              {item[name]}
                            </Typography>
                          )}
                        </Stack>
                        <Stack direction="row" gap="20px">
                          <EditIcon
                            id="editButton"
                            sx={{ cursor: "pointer" }}
                            onClick={() => onEdit(item)}
                          />
                          <DeleteIcon
                            sx={{ cursor: "pointer" }}
                            onClick={() => onDelete(item.id)}
                          />
                        </Stack>
                      </Stack>
                    )}
                  </Draggable>
                ))
              ) : (
                <Typography
                  variant="h6"
                  textAlign="center"
                  width="100%"
                  color="text.secondary"
                >
                  No items found
                </Typography>
              )}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        mt={2}
        mb={2}
        gap={2}
        flexWrap="wrap"
      >
        <Typography variant="body2" color="text.secondary">
          {filteredItems.length} {filteredItems.length === 1 ? "item" : "items"}
          {searchQuery && ` found`}
        </Typography>
        <Box display="flex" alignItems="center" gap={1}>
          <Typography variant="body2" color="text.secondary">
            Items per page:
          </Typography>
          <FormControl size="small">
            <Select
              value={pageSize}
              onChange={handlePageSizeChange}
              sx={{ minWidth: 70 }}
            >
              {pageSizeOptions.map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
        <Pagination
          count={totalPages || 1}
          page={currentPage}
          onChange={handlePageChange}
          color="primary"
          showFirstButton
          showLastButton
          disabled={totalPages <= 1}
        />
      </Box>
    </Stack>
  );
};

export default DraggableItemList;
